import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { fetchDepartments, fetchEmployees, saveDepartment } from "../../api/HrAPIs";
import { Empty, Field, HrFrame, HrGuard, inputClass, useHr } from "./hrUi";

export default function DepartmentsPage() {
  return (
    <HrGuard anyOf={["HR.ManageDepartments", "HR.ViewDepartmentEmployees", "HR.ViewEmployees"]}>
      <Body />
    </HrGuard>
  );
}

function Body() {
  const hr = useHr();
  const queryClient = useQueryClient();
  const departments = useQuery({ queryKey: ["hrDepartments"], queryFn: fetchDepartments });
  const employees = useQuery({ queryKey: ["hrEmployees", "managers"], queryFn: () => fetchEmployees({ pageSize: 100, status: "Active" }), enabled: hr.can("HR.ManageDepartments") });
  const [form, setForm] = useState({ name: "", code: "", parentDepartmentId: "", managerEmployeeId: "", isActive: true });
  const [editing, setEditing] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    try {
      await saveDepartment({
        name: form.name,
        code: form.code || null,
        parentDepartmentId: form.parentDepartmentId ? Number(form.parentDepartmentId) : null,
        managerEmployeeId: form.managerEmployeeId ? Number(form.managerEmployeeId) : null,
        isActive: form.isActive,
      }, editing);
      toast.success("تم حفظ القسم");
      setForm({ name: "", code: "", parentDepartmentId: "", managerEmployeeId: "", isActive: true });
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["hrDepartments"] });
    } catch (error) { toast.error(error.message); }
  };

  const rows = departments.data || [];
  return (
    <HrFrame title="الأقسام والفرق" subtitle="الأقسام والفرق الفرعية تُضاف من هنا بدون تعديل في الكود.">
      {rows.length === 0 && !departments.isLoading && <Empty text="لا توجد أقسام بعد." />}
      <div className="mb-4 grid gap-2">
        {rows.map((department) => (
          <div key={department.id} className="flex flex-col gap-1 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-bold">{department.name} {department.code ? `· ${department.code}` : ""}</p>
              <p className="text-sm text-slate-500">
                {department.parentDepartmentId ? "فريق فرعي" : "قسم"} · المدير: {department.managerName || "—"} · النشطون: {department.employeeCount}
                {!department.isActive && " · متوقف"}
              </p>
            </div>
            {hr.can("HR.ManageDepartments") && (
              <button type="button" className="text-sm text-indigo-600" onClick={() => { setEditing(department.id); setForm({ name: department.name, code: department.code || "", parentDepartmentId: department.parentDepartmentId || "", managerEmployeeId: department.managerEmployeeId || "", isActive: department.isActive }); }}>تعديل</button>
            )}
          </div>
        ))}
      </div>
      {hr.can("HR.ManageDepartments") && (
        <form onSubmit={submit} className="grid gap-3 rounded-2xl border bg-white p-4 md:grid-cols-2">
          <Field label="الاسم"><input className={inputClass} required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
          <Field label="الكود"><input className={inputClass} value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} /></Field>
          <Field label="قسم أب / فريق تابع">
            <select className={inputClass} value={form.parentDepartmentId} onChange={(event) => setForm({ ...form, parentDepartmentId: event.target.value })}>
              <option value="">قسم رئيسي</option>
              {rows.filter((item) => item.id !== editing).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="مدير القسم">
            <select className={inputClass} value={form.managerEmployeeId} onChange={(event) => setForm({ ...form, managerEmployeeId: event.target.value })}>
              <option value="">بدون</option>
              {(employees.data?.items || []).map((item) => <option key={item.id} value={item.id}>{item.fullName}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} /> نشط</label>
          <button className="rounded-xl bg-indigo-600 px-4 py-2 text-sm text-white">{editing ? "تحديث" : "إضافة"}</button>
        </form>
      )}
    </HrFrame>
  );
}
